import { FC, ReactNode, useEffect, useMemo } from 'react'
import { Skeleton } from 'antd'
import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'

import getColor from '~/lib/getColor'
import { getenv } from '~/settings'
import { useCreateStripeClientSecretMutation } from '~/gql/graphql'
import { AllowedStripePaymentTypes } from '~/core/StripeElements/AllowedStripePaymentTypes'

const stripePublishableKey = getenv('STRIPE_PUBLISHABLE_KEY')

interface StripeElementsProps {
    children?: ReactNode
    userId: number
    allowedPaymentTypes?: AllowedStripePaymentTypes[]
}

/**
 * Wrapper of original stripe's Elements component with fictiv-like brand styles, loading stripe and getting stripe client secret
 */
export const StripeElements: FC<StripeElementsProps> = ({
    children,
    userId,
    allowedPaymentTypes = ['card'],
}) => {
    const stripePromise = useMemo(() => loadStripe(stripePublishableKey), [])
    const [createStripeClientSecret, { data, loading }] =
        useCreateStripeClientSecretMutation({
            variables: {
                id: userId,
                supportedPaymentTypes: allowedPaymentTypes,
            },
        })

    const clientSecret = data?.user?.createStripeClientSecret

    useEffect(() => {
        createStripeClientSecret()
    }, [])

    if (loading || !clientSecret) {
        return <Skeleton active />
    }

    return (
        <Elements
            stripe={stripePromise}
            options={{
                clientSecret,
                appearance: {
                    variables: {
                        colorPrimary: getColor('primary5'),
                        borderRadius: '4px',
                        colorDanger: getColor('error6'),
                        fontFamily: 'Museo-Sans, Arial, Helvetica, sans-serif',
                        fontSizeSm: '14px',
                        fontSizeBase: '14px',
                    },
                    rules: {
                        '.Input:focus': {
                            border: '1px solid var(--colorPrimary)',
                            boxShadow: 'none',
                        },
                    },
                },
            }}
        >
            {children}
        </Elements>
    )
}
